import React, { ReactElement, useState, useEffect } from "react";
import Popover, { popoverClasses } from "@mui/material/Popover";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import Typography from "@mui/material/Typography";
import { Box } from "@pokemon/design-v2.box";
import { styled } from "@mui/material/styles";

const StyledPopover = styled(Popover)(({ theme }) => ({
  [`& .${popoverClasses.paper}`]: {
    background: theme.palette.text.primary,
    color: theme.palette.primary.contrastText
  }
}));

/**
 * Required props for the copy field
 */
type Props = {
  /**
   * The value to copy when you click the button.
   */
  value: string;
  /**
   * The value to display, if different from the copy value
   */
  displayValue?: ReactElement;
  /**
   * The message that is displayed on hover when a message can be copied.
   */
  copyMessage: string;
  /**
   * The message that is displayed on hover when the copy button is clicked.
   */
  copiedMessage: string;
};

/*
 * This class is a MUI component version of the popover that will be replaced with the design system equivalent.
 * This is needed because the current P!T popover does not render properly in the MUI grid (it gets cut off by the cell).
 */

export const CopyField = ({
  value,
  displayValue,
  copyMessage,
  copiedMessage
}: Props) => {
  const [isCopied, setIsCopied] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  useEffect(() => {
    setIsCopied(false);
  }, [value]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLSpanElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  return (
    <Box style={{ display: "flex", alignItems: "center" }}>
      {displayValue || value}
      <Box
        style={{ display: "flex" }}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      >
        <FileCopyIcon
          onClick={() => {
            try {
              navigator.clipboard.writeText(value || "");
              setIsCopied(true);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error(
                "An error occurred while copying to clipboard:",
                error
              );
            }
          }}
          width={25}
          style={{ color: "#9E9E9E", marginLeft: 4, width: 20 }}
        />
      </Box>
      <StyledPopover
        id={value}
        sx={{
          pointerEvents: "none"
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        onClose={() => {}}
        disableRestoreFocus
      >
        <Typography sx={{ padding: 1 }}>
          {isCopied ? copiedMessage : copyMessage}
        </Typography>
      </StyledPopover>
    </Box>
  );
};
